
import { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";

/** Account types from calie_account_types table (platform-level classification) */
type AccountType = 'platform_admin' | 'agency' | 'business_owner' | null;

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /** Account type from secure calie_account_types table */
  accountType: AccountType;
  userRole: string | null; // Legacy alias for accountType (mapped for compatibility)
  role: string | null; // Legacy alias for userRole
  roleError: string | null;
  activeTenantId: string | null;
  memberships: Array<{ tenant_id: string; role: string; is_creator: boolean }>;
  switchActiveTenant: (tenantId: string) => Promise<void>;
  hasRole: (role: string) => boolean;
  /** Check if user is a platform admin */
  isAdmin: () => boolean;
  /** Check if user is an agency account */
  isAgency: () => boolean;
  /** Check if user is a business owner */
  isBusinessOwner: () => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [accountType, setAccountType] = useState<AccountType>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<Array<{ tenant_id: string; role: string; is_creator: boolean }>>([]);
  const [bootstrapAttempted, setBootstrapAttempted] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session?.user?.id || 'no user');
        
        setSession(session);
        setUser(session?.user ?? null);

        if (event === 'SIGNED_IN') {
          toast({
            title: "Welcome back!",
            description: "You have successfully signed in.",
          });
        }
        
        if (event === 'SIGNED_OUT') {
          toast({
            title: "Signed out",
            description: "You have been signed out successfully.",
          });
        }

        if (session?.user) {
          Promise.all([
            fetchAccountType(session.user.id),
            fetchActiveTenant(session.user.id),
            fetchMemberships(session.user.id),
          ]).finally(() => setLoading(false));
        } else {
          setAccountType(null);
          setRoleError(null);
          setActiveTenantId(null);
          setMemberships([]);
          clearSentryUser();
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error("Error getting session:", error);
        // Handle specific auth errors
        if (error.message?.includes('refresh_token_not_found')) {
          console.log('Refresh token not found, user needs to re-authenticate');
          supabase.auth.signOut();
        }
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        Promise.all([
          fetchAccountType(session.user.id),
          fetchActiveTenant(session.user.id),
          fetchMemberships(session.user.id),
        ]).finally(() => setLoading(false));
      } else {
        setAccountType(null);
        setRoleError(null);
        setActiveTenantId(null);
        setMemberships([]);
        clearSentryUser();
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [toast]);

  /** Fetch account type from secure calie_get_account_type() RPC */
  const fetchAccountType = async (userId: string) => {
    try {
      setRoleError(null);
      const { data, error } = await supabase.rpc("calie_get_account_type", { _user_id: userId });
      
      if (error) {
        console.error("Error fetching account type:", error);
        setAccountType(null);
        setRoleError(`Account type fetch error: ${error.message}`);
      } else if (!data) {
        // Attempt bootstrap if no account type exists
        if (!bootstrapAttempted) {
          setBootstrapAttempted(true);
          const { error: rpcError } = await supabase.rpc("calie_create_workspace", { name: "Default Workspace" });
          if (rpcError) {
            console.warn("Bootstrap RPC failed:", rpcError.message);
          } else {
            // Retry fetching account type after bootstrap
            const { data: retryData, error: retryError } = await supabase.rpc("calie_get_account_type", { _user_id: userId });
            if (!retryError && retryData) {
              setAccountType(retryData as AccountType);
              setRoleError(null);
              await fetchMemberships(userId);
              return;
            }
          }
        }
        console.warn("No account type found for user:", userId);
        setAccountType('business_owner'); // Default account type
        setRoleError("Account type not found");
      } else {
        setAccountType(data as AccountType);
        setRoleError(null);
      }
    } catch (error) {
      console.error("Error in fetchAccountType:", error);
      setRoleError("Unexpected error fetching account type");
    }
  };

  /** Fetch active tenant from calie_users table */
  const fetchActiveTenant = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("calie_users")
        .select("tenant_id, active_tenant_id")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching active tenant:", error);
        setActiveTenantId(null);
      } else if (data) {
        setActiveTenantId(data.active_tenant_id ?? data.tenant_id);
      }
    } catch (error) {
      console.error("Error in fetchActiveTenant:", error);
    }
  };

  const fetchMemberships = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("calie_tenant_members")
        .select("tenant_id, role, is_creator")
        .eq("user_id", userId);
      if (error) {
        console.error("Error fetching memberships:", error);
        setMemberships([]);
      } else {
        setMemberships(data || []);
      }
    } catch (e) {
      console.error("Unexpected error fetching memberships", e);
      setMemberships([]);
    }
  };

  const switchActiveTenant = async (tenantId: string) => {
    await supabase.rpc("calie_set_active_tenant", { p_tenant_id: tenantId }).then(() => {
      if (user?.id) {
        Promise.all([
          fetchAccountType(user.id),
          fetchActiveTenant(user.id),
          fetchMemberships(user.id),
        ]);
      }
    });
  };

  // Update Sentry user context when user data changes
  useEffect(() => {
    if (user && activeTenantId) {
      setSentryUser({
        id: user.id,
        email: user.email,
        tenantId: activeTenantId,
      });
    }
  }, [user, activeTenantId]);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: "Error signing out",
        description: "There was a problem signing you out.",
        variant: "destructive",
      });
    }
  };

  /** Map account type to legacy role string for backward compatibility */
  const userRole = accountType === 'platform_admin' ? 'admin' : accountType;
  
  /** Check if user has a specific role (legacy compatibility) */
  const hasRole = (role: string): boolean => userRole === role;
  /** Check if user is a platform admin */
  const isAdmin = (): boolean => accountType === 'platform_admin';
  /** Check if user is an agency account */
  const isAgency = (): boolean => accountType === 'agency';
  /** Check if user is a business owner */
  const isBusinessOwner = (): boolean => accountType === 'business_owner';

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      loading,
      accountType,
      userRole, 
      role: userRole, // Legacy alias
      roleError, 
      activeTenantId, 
      memberships, 
      switchActiveTenant, 
      hasRole, 
      isAdmin,
      isAgency, 
      isBusinessOwner, 
      signOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
